import { useQuery } from '@tanstack/react-query';
import { api, ErroApi } from '../../nucleo/api';

export type EstadoOportunidade = 'aberta' | 'ganha' | 'perdida';
export type TipoAtividade = 'tarefa' | 'ligacao' | 'reuniao' | 'follow_up';
export const TIPOS_ATIVIDADE: TipoAtividade[] = ['tarefa', 'ligacao', 'reuniao', 'follow_up'];

export interface Etapa {
  id: string;
  nome: string;
  tipo: 'aberta' | 'ganho' | 'perdido';
  ordem: number;
  probabilidade: number;
  abertas?: number;
}

export interface OportunidadeResumo {
  id: string;
  revisao?: string;
  codigo: string;
  titulo: string;
  parceiro_id: string | null;
  parceiro: string | null;
  valor_centavos: string;
  moeda: string;
  etapa_id: string;
  etapa: string;
  estado: EstadoOportunidade;
  probabilidade: number;
  responsavel_id: string | null;
  responsavel: string | null;
  equipe_id: string | null;
  origem: string | null;
  canal: string | null;
  previsao_fechamento: string | null;
  etapa_desde: string;
  atividades_pendentes: number;
  empresa_id: string | null;
  /** campos personalizados (N8) */
  extras: Record<string, unknown>;
  created_at: string;
}

export interface Metricas {
  abertas: number;
  valor_negociacao: { moeda: string; centavos: string }[];
  conversao: { etapa_id: string; nome: string; entraram: number; avancaram: number; taxa: number | null }[];
  ciclo_medio_dias: number | null;
  ganhas: number;
  perdidas: number;
  taxa_vitoria: number | null;
}

export interface AtividadeLinha {
  id: string;
  tipo: TipoAtividade;
  titulo: string;
  descricao: string | null;
  inicio: string;
  fim: string | null;
  estado: 'pendente' | 'concluida';
  resultado: string | null;
  responsavel_id: string;
  responsavel: string | null;
  oportunidade_id: string | null;
  oportunidade_codigo: string | null;
  oportunidade: string | null;
  parceiro: string | null;
  atrasada: boolean;
  created_at: string;
}

export interface Vendedor {
  id: string;
  nome: string;
  email: string;
}

export interface Motivo {
  id: string;
  nome: string;
  uso: number;
  valor_perdido_centavos: string;
}

export interface Equipe {
  id: string;
  nome: string;
  lider_id: string | null;
  lider: string | null;
  membros: string[];
  membros_nomes: { id: string; nome: string }[];
  abertas: number;
  valor_aberto_centavos: string;
}

/** Centavos (texto) para moeda local, sem float na conta: só na exibição. */
export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR', compacto = false): string {
  if (centavos === null || centavos === undefined) return '';
  const c = BigInt(centavos);
  const v = Number(c / 100n) + Number(c % 100n) / 100;
  return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda, ...(compacto ? { notation: 'compact', maximumFractionDigits: 1 } : {}) }).format(v);
}

/** Texto em reais ('1.234,56') para centavos inteiros em texto, sem float. */
export function reaisParaCentavos(texto: string): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  return (BigInt(m[1]!.replace(/\./g, '')) * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0')).toString();
}

export function centavosParaReais(centavos: string): string {
  const c = BigInt(centavos);
  return `${c / 100n},${String(c % 100n).padStart(2, '0')}`;
}

export const formatarData = (iso: string | null, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma).format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : '');
export const formatarDataHora = (iso: string | null, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : '');

/** datetime-local (hora local do navegador) para ISO com fuso. */
export function localParaIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const useEtapas = () => useQuery({ queryKey: ['pipeline', 'etapas'], queryFn: () => api<Etapa[]>('/api/v1/pipeline/etapas') });
export const useVendedores = () => useQuery({ queryKey: ['pipeline', 'vendedores'], queryFn: () => api<Vendedor[]>('/api/v1/pipeline/vendedores'), staleTime: 60_000 });
export const useMotivos = () => useQuery({ queryKey: ['pipeline', 'motivos'], queryFn: () => api<Motivo[]>('/api/v1/pipeline/motivos-perda') });
export const useEquipes = () => useQuery({ queryKey: ['pipeline', 'equipes'], queryFn: () => api<Equipe[]>('/api/v1/pipeline/equipes') });
/** Clientes pela rota HTTP de Cadastros (sem importar código do outro módulo). */
export const useClientes = () =>
  useQuery({ queryKey: ['pipeline', 'clientes-opcoes'], queryFn: () => api<{ itens: { id: string; nome: string }[] }>('/api/v1/cadastros/parceiros?aba=clientes&ordenar=nome&limite=200'), staleTime: 60_000 });

/** Revision captured when the action opens. A missing revision never sends a blind command. */
export function comRevisaoOportunidade(revisao:unknown):{revisao_esperada:string}{
 if(typeof revisao!=='string'||!revisao.trim()||revisao.length>512)throw new ErroApi(0,'revisao_obrigatoria','Leia a oportunidade atual antes de alterar.');
 return {revisao_esperada:revisao};
}
